/**
 * The Compendium (rules v0.6 Appendix B), encoded as data.
 *
 * Owners: EM Emergency · SURG Surgeon · PHYS Physician · PSYCH Psychiatrist ·
 * ICU Intensivist · ANY anyone. A Theatre step is performed by the Surgeon and
 * needs the Intensivist's Anaesthetise in the same turn.
 *
 * Additions beyond v0.6 (DESIGN D-12, D-16, D-17):
 *   - 'very-hairy'           front alias of Enthusiastic Beard
 *   - 'hat-adhesion-tophat'  the Mayor's Cousin (Green)
 *   - 'sync-hiccups'         the Choir (Green)
 */
import type { Diagnosis, Owner, Rate, Risk, Route, RouteCode, Step, BedKind } from '../types.ts';

/**
 * What happens if a route is run on the wrong diagnosis, in plain words
 * (rules/outcomes.ts applyRiskIfWrong: NONE wastes the action, LOW ticks the
 * clock once, HIGH deteriorates, CRITICAL kills).
 */
export const RISK_IF_WRONG: Record<Risk, string> = {
  NONE: 'the action is wasted',
  LOW: 'their waiting clock moves on one step',
  HIGH: 'they get worse',
  CRITICAL: 'they die',
};

// ─── step builders ───

const STAB_BY: Owner[] = ['EM', 'PHYS', 'ICU'];

function stab(label = 'Stabilise', by: Owner[] = STAB_BY): Step[] {
  return [{ kind: 'STABILISE', label, by }];
}
/** A medication/therapy step, repeated `times` (the ⚠, if any, sits on the last copy). */
function tx(label: string, by: Owner[], opts: { times?: number; warn?: boolean; inICU?: boolean } = {}): Step[] {
  const n = opts.times ?? 1;
  return Array.from({ length: n }, (_, i) => ({
    kind: 'TREAT' as const,
    label: n > 1 ? `${label} ${i + 1}` : label,
    by,
    warn: opts.warn && i === n - 1 ? true : undefined,
    inICU: opts.inICU,
  }));
}
function reassure(label = 'Reassure'): Step[] {
  return [{ kind: 'REASSURE', label, by: ['ANY'] }];
}
function theatre(label: string, opts: { warn?: boolean; blood?: number } = {}): Step[] {
  return [{ kind: 'THEATRE', label: `Theatre: ${label}`, by: ['SURG'], warn: opts.warn, blood: opts.blood }];
}
function icu(n: number): Step[] {
  return Array.from({ length: n }, (_, i) => ({ kind: 'ICU' as const, label: n > 1 ? `ICU ${i + 1}` : 'ICU', by: ['ICU' as Owner] }));
}
function stepDown(bed: BedKind): Step[] {
  return [{ kind: 'STEP_DOWN', label: `Step-down ${bed}`, by: ['ICU'], bed }];
}
function observe(n: number, bed: BedKind | 'any'): Step[] {
  const where = bed === 'any' ? 'any bed' : bed;
  return Array.from({ length: n }, (_, i) => ({
    kind: 'OBSERVE' as const,
    label: n > 1 ? `Observe ${i + 1} (${where})` : `Observe (${where})`,
    by: ['ANY' as Owner],
    bed,
  }));
}
function scanStep(): Step[] {
  return [{ kind: 'SCAN', label: 'Scan', by: ['ANY'] }];
}
function discharge(label = 'Discharge'): Step[] {
  return [{ kind: 'DISCHARGE', label, by: ['ANY'] }];
}

function route(
  code: RouteCode,
  steps: Step[][],
  turns: number,
  uses: string[],
  riskIfWrong: Risk,
  complRate: Rate | null,
): Route {
  return { code, steps: steps.flat(), turns, uses, riskIfWrong, complRate };
}

const EM_PHYS: Owner[] = ['EM', 'PHYS'];
const PHYS: Owner[] = ['PHYS'];
const PSYCH: Owner[] = ['PSYCH'];

// ─── entries (alphabetical, as the printed book) ───

export const COMPENDIUM: Diagnosis[] = [
  {
    key: 'actual-extra-limb', name: 'Actual Extra Limb', colour: 'AMBER', page: 3,
    differentials: ['phantom-limb'], definitive: 'ASSESS', ruleOuts: { 'phantom-limb': 'ASSESS' },
    routes: [
      route('S', [theatre('Removal', { warn: true, blood: 1 }), observe(1, 'SW'), discharge()], 2, ['Theatre', 'SW'], 'HIGH', 'MED'),
      route('P', [tx('Learn to Love It', PSYCH, { times: 3 }), observe(2, 'QW'), discharge()], 5, ['QW'], 'NONE', null),
    ],
    notes: 'Removal of a limb that is not there is the HIGH.',
  },
  {
    key: 'brand-new-person', name: 'Brand New Person', colour: 'GREEN', page: 4, special: 'newborn',
    differentials: [], definitive: null, ruleOuts: {},
    routes: [route('G', [reassure(), discharge('Discharge with parent')], 1, [], 'NONE', null)],
    notes: 'Special card; appears when a Caesarean completes.',
  },
  {
    key: 'breech', name: 'Breech of Etiquette', colour: 'RED', page: 5, vent: true,
    differentials: ['imminent-arrival'], definitive: 'SCAN', ruleOuts: { 'imminent-arrival': 'ASSESS' },
    routes: [
      route('S', [stab(), theatre('Caesarean', { warn: true, blood: 2 }), icu(1), stepDown('SW'), observe(1, 'SW'), discharge()],
        5, ['Theatre', 'ICU', 'SW'], 'CRITICAL', 'LOW'),
    ],
    notes: 'The obstetric registrar advises by phone; the Surgeon operates. A Brand New Person arrives when the Caesarean completes.',
  },
  {
    key: 'jazz-hands', name: 'Chronic Jazz Hands', colour: 'GREEN', page: 6,
    differentials: ['hiccups-severe'], definitive: 'ASSESS', ruleOuts: { 'hiccups-severe': 'ASSESS' },
    routes: [route('G', [reassure(), tx('Stillness', EM_PHYS), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'cranial-barnacle', name: 'Cranial Barnacle', colour: 'AMBER', page: 7, special: 'exit-only',
    differentials: [], definitive: 'SCAN', ruleOuts: {},
    routes: [route('S', [theatre('Scrape', { warn: true }), discharge()], 1, ['Theatre'], 'HIGH', 'MED')],
    notes: 'Exit preset only; never printed on a front.',
  },
  {
    key: 'enthusiastic-beard', name: 'Enthusiastic Beard', colour: 'GREEN', page: 8,
    differentials: ['lycanthropy'], definitive: 'ASSESS', ruleOuts: { lycanthropy: 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'very-hairy', name: 'Enthusiastic Beard', frontName: 'Very Hairy', colour: 'GREEN', page: 8,
    differentials: ['lycanthropy'], definitive: 'ASSESS', ruleOuts: { lycanthropy: 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
    notes: 'Front alias of Enthusiastic Beard (D-12).',
  },
  {
    key: 'dread-acute', name: 'Existential Dread, acute on chronic', colour: 'AMBER', page: 9, psychOnly: true,
    differentials: ['thyroid-storm'], definitive: 'OPINION_PSYCH', ruleOuts: { 'thyroid-storm': 'OPINION_PHYS' },
    routes: [
      route('M', [tx('Perspective infusion', PHYS, { warn: true }), icu(1), stepDown('MW'), discharge()], 3, ['ICU', 'MW'], 'HIGH', 'MED'),
      route('P', [tx('Perspective', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'LOW', null),
    ],
  },
  {
    key: 'dread-florid', name: 'Existential Dread, florid', colour: 'RED', page: 10, psychOnly: true,
    differentials: ['thyroid-storm'], definitive: 'OPINION_PSYCH', ruleOuts: { 'thyroid-storm': 'OPINION_PHYS' },
    routes: [route('P', [stab(), tx('Perspective', PSYCH, { times: 2 }), observe(2, 'QW'), discharge()], 4, ['QW'], 'LOW', null)],
    notes: 'The one Red the Psychiatrist runs. No ICU.',
  },
  {
    key: 'deja-vu', name: 'Full-Body Déjà Vu', colour: 'AMBER', page: 11,
    differentials: ['silent-stroke'], definitive: 'SCAN', ruleOuts: { 'silent-stroke': 'OPINION_PHYS' },
    routes: [
      route('M', [tx('Novelty', PHYS, { warn: true }), observe(1, 'MW'), discharge()], 2, ['MW'], 'LOW', 'LOW'),
      route('P', [tx('Accept the Loop', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'NONE', null),
    ],
  },
  {
    key: 'hat-adhesion', name: 'Hat Adhesion', colour: 'AMBER', page: 12,
    differentials: ['scalp-fusion'], definitive: 'ASSESS', ruleOuts: { 'scalp-fusion': 'ASSESS' },
    routes: [
      route('S', [theatre('Release', { warn: true }), discharge()], 1, ['Theatre'], 'HIGH', 'MED'),
      route('M', [tx('Solvent', EM_PHYS, { warn: true }), observe(1, 'any'), discharge()], 2, ['a bed'], 'LOW', 'LOW'),
      route('P', [tx('Become a Hat Person', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'NONE', null),
    ],
  },
  {
    key: 'hat-adhesion-tophat', name: 'Hat Adhesion, top hat', colour: 'GREEN', page: 12, special: 'vip',
    differentials: ['scalp-fusion'], definitive: 'ASSESS', ruleOuts: { 'scalp-fusion': 'ASSESS' },
    routes: [route('G', [tx('Solvent', EM_PHYS), discharge()], 1, ['bay'], 'LOW', null)],
    notes: 'The Mayor’s Cousin (D-16). The top hat has taken at the brim and will not come off; it is, he will tell you, a very good hat. Solvent at the bedside lifts it in one go and he can walk home in it. He is connected and expects priority; explain how the team prioritises clinical need. His care follows the ordinary patient rules. If the brim has taken the scalp with it, that is Scalp Fusion (Assess settles it), an Amber like anyone else’s.',
  },
  {
    key: 'imminent-arrival', name: 'Imminent Arrival', colour: 'GREEN', page: 13,
    differentials: ['breech'], definitive: 'SCAN', ruleOuts: { breech: 'ASSESS' },
    routes: [route('G', [reassure(), discharge('Discharge to the Birthing Suite')], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'indigestion', name: 'Indigestion', colour: 'GREEN', page: 14,
    differentials: ['swallowed-ambition'], definitive: 'SCAN', ruleOuts: { 'swallowed-ambition': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'lycanthropy', name: 'Pre-Lunar Lycanthropy', colour: 'RED', page: 19, vent: true,
    differentials: ['enthusiastic-beard'], definitive: 'ASSESS', ruleOuts: { 'enthusiastic-beard': 'ASSESS' },
    routes: [
      route('S', [stab(), theatre('De-fanging', { warn: true, blood: 2 }), icu(1), stepDown('SW'), tx('Wolfsbane', PHYS, { times: 2 }), observe(1, 'SW'), discharge()],
        6, ['Theatre', 'ICU', 'SW'], 'CRITICAL', 'LOW'),
      route('M', [stab(), icu(2), tx('Wolfsbane', PHYS, { times: 3, inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        6, ['ICU ×2', 'MW'], 'HIGH', 'MED'),
    ],
    notes: 'Wolfsbane may be given in ICU; the Physician spends the action, the Intensivist still Supports. Both routes take six turns.',
  },
  {
    key: 'mild-glowing', name: 'Mild Glowing', colour: 'AMBER', page: 15,
    differentials: ['radiant-fever', 'sequins'], definitive: 'SCAN', ruleOuts: { 'radiant-fever': 'OPINION_PHYS', sequins: 'ASSESS' },
    routes: [
      route('M', [tx('Dimming', PHYS, { warn: true }), observe(1, 'MW'), discharge()], 2, ['MW'], 'LOW', 'LOW'),
      route('P', [tx('Embrace the Glow', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'NONE', null),
    ],
  },
  {
    key: 'partial-invisibility', name: 'Partial Invisibility', colour: 'AMBER', page: 16,
    differentials: ['sock-loss'], definitive: 'ASSESS', ruleOuts: { 'sock-loss': 'ASSESS' },
    routes: [route('M', [tx('Opacity', PHYS, { times: 2, warn: true }), discharge()], 2, ['MW'], 'LOW', 'LOW')],
  },
  {
    key: 'phantom-limb', name: 'Phantom Limb, extra', colour: 'AMBER', page: 17,
    differentials: ['actual-extra-limb'], definitive: 'ASSESS', ruleOuts: { 'actual-extra-limb': 'ASSESS' },
    routes: [
      route('P', [tx('Grounding', PSYCH, { times: 2 }), discharge()], 1, ['QW'], 'NONE', null),
      route('S', [theatre('Removal', { warn: true, blood: 1 }), observe(1, 'SW'), discharge()], 2, ['Theatre', 'SW'], 'HIGH', 'MED'),
    ],
    notes: 'The 50/50 card. One bedside Assess settles it. Operating on a phantom is the HIGH.',
  },
  {
    key: 'phantom-pregnancy', name: 'Phantom Pregnancy, male', colour: 'AMBER', page: 18, psychOnly: true,
    differentials: ['swallowed-ambition'], definitive: 'OPINION_PSYCH', ruleOuts: { 'swallowed-ambition': 'SCAN' },
    routes: [route('P', [tx('Reassurance', PSYCH, { times: 2 }), discharge()], 1, ['QW'], 'NONE', null)],
  },
  {
    key: 'radiant-fever', name: 'Radiant Fever', colour: 'RED', page: 20, vent: true,
    differentials: ['mild-glowing'], definitive: 'SCAN', ruleOuts: { 'mild-glowing': 'OPINION_PHYS' },
    routes: [
      route('M', [stab(), icu(1), tx('Cooling infusion', PHYS, { times: 2, inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        5, ['ICU', 'MW'], 'HIGH', 'LOW'),
    ],
  },
  {
    key: 'hiccups-mild', name: 'Reverse Hiccups, mild', colour: 'GREEN', page: 21,
    differentials: ['hiccups-severe'], definitive: 'ASSESS', ruleOuts: { 'hiccups-severe': 'ASSESS' },
    routes: [route('G', [tx('Forwardness', EM_PHYS), discharge()], 1, ['bay'], 'LOW', null)],
  },
  {
    key: 'hiccups-severe', name: 'Reverse Hiccups, severe', colour: 'AMBER', page: 22,
    differentials: ['jazz-hands'], definitive: 'ASSESS', ruleOuts: { 'jazz-hands': 'ASSESS' },
    routes: [
      route('M', [tx('Forwardness', EM_PHYS, { times: 2, warn: true }), observe(1, 'any'), discharge()], 2, ['a bed'], 'LOW', 'LOW'),
      route('P', [tx('Hiccup Acceptance', PSYCH, { times: 2 }), observe(1, 'QW'), discharge()], 2, ['QW'], 'NONE', null),
    ],
  },
  {
    key: 'scalp-fusion', name: 'Scalp Fusion', colour: 'AMBER', page: 23,
    differentials: ['hat-adhesion'], definitive: 'ASSESS', ruleOuts: { 'hat-adhesion': 'ASSESS' },
    routes: [
      route('S', [theatre('Release', { warn: true, blood: 1 }), observe(1, 'SW'), discharge()], 2, ['Theatre', 'SW'], 'HIGH', 'MED'),
      route('M', [tx('Solvent', PHYS, { times: 3, warn: true }), observe(2, 'MW'), discharge()], 4, ['MW'], 'LOW', 'LOW'),
    ],
  },
  {
    key: 'sequins', name: 'Sequins', colour: 'GREEN', page: 24,
    differentials: ['mild-glowing'], definitive: 'SCAN', ruleOuts: { 'mild-glowing': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'silent-stroke', name: 'Silent Stroke-of-Genius', colour: 'RED', page: 25, vent: true,
    differentials: ['spontaneous-mime', 'deja-vu'], definitive: 'SCAN', ruleOuts: {},
    routes: [
      route('M', [stab(), icu(1), tx('Humility infusion', PHYS, { inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        5, ['ICU', 'MW'], 'HIGH', 'LOW'),
    ],
    notes: 'A Red is never the thing you rule out; Scan settles it.',
  },
  {
    key: 'sock-loss', name: 'Sock Loss Syndrome', colour: 'GREEN', page: 26,
    differentials: ['partial-invisibility'], definitive: 'ASSESS', ruleOuts: { 'partial-invisibility': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'combustion', name: 'Spontaneous Combustion, mild', colour: 'RED', page: 27, vent: true,
    differentials: ['sunburn'], definitive: 'ASSESS', ruleOuts: { sunburn: 'ASSESS' },
    routes: [
      route('S', [stab('Stabilise: Damp', ['EM']), theatre('Debridement', { warn: true, blood: 2 }), icu(1), stepDown('SW'), observe(1, 'SW'), discharge()],
        5, ['Theatre', 'ICU', 'SW'], 'HIGH', 'LOW'),
      route('M', [stab('Stabilise: Damp', ['EM']), icu(2), tx('Damp', PHYS, { times: 2, inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        6, ['ICU', 'MW'], 'HIGH', 'MED'),
    ],
    notes: 'Damp must be the first step; it is the Stabilise (Emergency only).',
  },
  {
    key: 'spontaneous-mime', name: 'Spontaneous Mime', colour: 'AMBER', page: 28,
    differentials: ['silent-stroke', 'sulking'], definitive: 'SCAN', ruleOuts: { 'silent-stroke': 'OPINION_PHYS', sulking: 'ASSESS' },
    routes: [
      route('M', [tx('Loud medication', PHYS, { times: 2, warn: true }), discharge()], 2, ['MW'], 'LOW', 'LOW'),
      route('P', [tx('Volume', PSYCH), observe(1, 'QW'), discharge()], 2, ['QW'], 'LOW', null),
    ],
  },
  {
    key: 'sulking', name: 'Sulking', colour: 'GREEN', page: 29,
    differentials: ['spontaneous-mime'], definitive: 'OPINION_PSYCH', ruleOuts: { 'spontaneous-mime': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'sunburn', name: 'Sunburn', colour: 'GREEN', page: 30,
    differentials: ['combustion'], definitive: 'ASSESS', ruleOuts: { combustion: 'ASSESS' },
    routes: [route('G', [tx('Cooling', EM_PHYS), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'swallowed-ambition', name: 'Swallowed Ambition', colour: 'AMBER', page: 31,
    differentials: ['indigestion'], definitive: 'SCAN', ruleOuts: { indigestion: 'ASSESS' },
    routes: [
      route('S', [scanStep(), theatre('Retrieval', { warn: true, blood: 1 }), discharge()], 2, ['Scanner', 'Theatre'], 'HIGH', 'MED'),
      route('M', [observe(3, 'MW'), discharge()], 3, ['MW'], 'NONE', null),
    ],
    notes: 'The M route has no ⚠, so the only thing that can go wrong is skipping the Observes and reading the EARLY line. Tempting.',
  },
  {
    key: 'sync-hiccups', name: 'Synchronised Hiccups', colour: 'GREEN', page: 32, special: 'choir',
    differentials: ['silent-stroke'], definitive: 'SCAN', ruleOuts: { 'silent-stroke': 'OPINION_PHYS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
    notes: 'The Choir (D-17). Four choristers off one bus, hiccupping in close harmony and on the beat. Hiccups are hiccups: reassure, then home. But a hiccup that has stopped keeping time is not a hiccup. A Silent Stroke-of-Genius looks exactly like the others from the end of the bed; a Scan settles it and a Physician’s opinion rules it out. Four identical fronts invite one decision for all four. Make four.',
  },
  {
    key: 'thyroid-storm', name: 'Thyroid Storm of Ideas', colour: 'RED', page: 33, vent: true,
    differentials: ['dread-acute'], definitive: 'OPINION_PHYS', ruleOuts: { 'dread-acute': 'OPINION_PSYCH' },
    routes: [
      route('M', [stab(), icu(1), tx('Quieting infusion', PHYS, { times: 2, inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        5, ['ICU', 'MW'], 'HIGH', 'LOW'),
    ],
  },

  // ─── Supplement (Oct 2026): nine more families, pages 34–42 ───
  // Built to give the Intensivist and the Surgeon more of the night's work, and
  // in the book's habit of a harmless front with a dangerous card behind it.
  {
    key: 'loose-shadow', name: 'Loose Shadow', colour: 'GREEN', page: 34,
    differentials: ['shadow-detachment'], definitive: 'ASSESS', ruleOuts: { 'shadow-detachment': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
    notes: 'The shadow lags a step behind. If it has stopped following altogether, turn to the next page.',
  },
  {
    key: 'shadow-detachment', name: 'Shadow Detachment', colour: 'RED', page: 35, vent: true,
    differentials: ['loose-shadow'], definitive: 'SCAN', ruleOuts: { 'loose-shadow': 'ASSESS' },
    routes: [
      route('S', [stab(), theatre('Reattachment', { warn: true, blood: 1 }), icu(1), stepDown('SW'), observe(1, 'SW'), discharge()],
        4, ['Theatre', 'ICU', 'SW'], 'CRITICAL', 'LOW'),
      route('M', [stab(), icu(2), tx('Gloom infusion', PHYS, { inICU: true, warn: true }), stepDown('MW'), discharge()],
        4, ['ICU ×2', 'MW'], 'HIGH', 'MED'),
    ],
    notes: 'The surgical route is quicker but needs Theatre and an anaesthetist in the same turn; the medical route holds an ICU bed for two nights.',
  },
  {
    key: 'static-hair', name: 'Static Hair', colour: 'AMBER', page: 36,
    differentials: ['internal-thunderstorm'], definitive: 'SCAN', ruleOuts: { 'internal-thunderstorm': 'OPINION_PHYS' },
    routes: [
      route('M', [tx('Grounding', EM_PHYS, { warn: true }), observe(1, 'any'), discharge()], 2, ['a bed'], 'LOW', 'LOW'),
      route('P', [tx('Lean Into It', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'NONE', null),
    ],
  },
  {
    key: 'internal-thunderstorm', name: 'Internal Thunderstorm', colour: 'RED', page: 37, vent: true,
    differentials: ['static-hair'], definitive: 'OPINION_PHYS', ruleOuts: { 'static-hair': 'SCAN' },
    routes: [
      route('M', [stab(), icu(2), tx('Earthing infusion', PHYS, { times: 2, inICU: true, warn: true }), stepDown('MW'), observe(1, 'MW'), discharge()],
        5, ['ICU ×2', 'MW'], 'HIGH', 'MED'),
    ],
    notes: 'No surgical option: two ICU nights, the Physician giving the infusions and the Intensivist Supporting.',
  },
  {
    key: 'stuck-kazoo', name: 'Stuck Kazoo', colour: 'AMBER', page: 38,
    differentials: ['indigestion'], definitive: 'SCAN', ruleOuts: { indigestion: 'ASSESS' },
    routes: [
      route('S', [theatre('Retrieval', { warn: true }), discharge()], 1, ['Theatre'], 'HIGH', 'MED'),
      route('M', [observe(2, 'SW'), discharge()], 3, ['SW'], 'LOW', null),
    ],
    notes: 'Most kazoos pass on their own with two nights on the surgical ward. Theatre is quick if the ward needs the bed.',
  },
  {
    key: 'melancholy-fog', name: 'Melancholy Fog', colour: 'AMBER', page: 39,
    differentials: ['internal-thunderstorm'], definitive: 'ASSESS', ruleOuts: { 'internal-thunderstorm': 'OPINION_PHYS' },
    routes: [
      route('P', [tx('Sunshine Talk', PSYCH), observe(2, 'QW'), discharge()], 3, ['QW'], 'NONE', null),
      route('M', [tx('Umbrella course', PHYS, { warn: true }), observe(1, 'MW'), discharge()], 2, ['MW'], 'LOW', 'LOW'),
    ],
  },
  {
    key: 'persistent-yodel', name: 'Persistent Yodel', colour: 'GREEN', page: 40,
    differentials: ['hiccups-severe'], definitive: 'ASSESS', ruleOuts: { 'hiccups-severe': 'ASSESS' },
    routes: [route('G', [reassure(), tx('Hush', EM_PHYS), discharge()], 1, ['bay'], 'NONE', null)],
  },
  {
    key: 'creased-elbows', name: 'Creased Elbows', colour: 'GREEN', page: 41,
    differentials: ['acute-origami'], definitive: 'ASSESS', ruleOuts: { 'acute-origami': 'ASSESS' },
    routes: [route('G', [reassure(), discharge()], 1, ['bay'], 'NONE', null)],
    notes: 'A crease is a crease. A crane is the facing page.',
  },
  {
    key: 'acute-origami', name: 'Acute Origami', colour: 'RED', page: 42,
    differentials: ['creased-elbows'], definitive: 'SCAN', ruleOuts: { 'creased-elbows': 'ASSESS' },
    routes: [
      route('S', [stab(), theatre('Unfolding', { warn: true, blood: 1 }), icu(1), stepDown('SW'), observe(1, 'SW'), discharge()],
        4, ['Theatre', 'ICU', 'SW'], 'CRITICAL', 'LOW'),
      route('M', [stab(), tx('Flattening', PHYS, { times: 2, warn: true }), observe(1, 'MW'), discharge()],
        3, ['MW'], 'HIGH', 'MED'),
    ],
  },
];

/** Exploratory Polishing: E17's pseudo-route. Always wrong; risk HIGH. */
export const EXPLORATORY_POLISHING: Route = route('X', [theatre('Exploratory Polishing')], 1, ['Theatre'], 'HIGH', null);

const BY_KEY: Record<string, Diagnosis> = Object.fromEntries(COMPENDIUM.map((d) => [d.key, d]));

export function diag(key: string): Diagnosis {
  const d = BY_KEY[key];
  if (!d) throw new Error(`Unknown diagnosis '${key}'`);
  return d;
}

export function hasDiag(key: string): boolean {
  return key in BY_KEY;
}

/** Diagnoses whose name contains "Glowing" — the subject of E17's decree. */
export const GLOWING_KEYS = ['mild-glowing', 'radiant-fever'];

/** Diagnoses with a page in the printed book (aliases share their parent's page). */
export function bookEntries(): Diagnosis[] {
  return COMPENDIUM.filter((d) => !d.frontName);
}
